import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Vertrag } from '../types';
import AktivitaetenBlock from './AktivitaetenBlock';

const API_URL = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8000';

// ── helpers ────────────────────────────────────────────────────────────────

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('de-DE');
}

function formatBetrag(value: number | null): string {
  if (value == null) return '—';
  return value.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
}

function daysUntil(iso: string | null): number | null {
  if (!iso) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const target = new Date(iso);
  target.setHours(0, 0, 0, 0);
  return Math.ceil((target.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
}

// ── chips ──────────────────────────────────────────────────────────────────

const TYP_CHIP: Record<string, React.CSSProperties> = {
  EINKAUF: { background: 'var(--bone-2)', color: 'var(--green-900)', border: '1px solid var(--green-400)' },
  VERKAUF: { background: 'var(--bone-2)', color: 'var(--green-500)', border: '1px solid var(--green-400)' },
};

const STATUS_CHIP: Record<string, React.CSSProperties> = {
  ENTWURF:   { background: 'var(--bone-2)', color: 'var(--ink-2)', border: '1px solid var(--bone-3)' },
  AKTIV:     { background: 'var(--bone-2)', color: 'var(--green-500)', border: '1px solid var(--green-400)' },
  GEKUENDIGT:{ background: 'var(--bone-2)', color: 'var(--red)', border: '1px solid var(--red)' },
  ABGELAUFEN:{ background: 'var(--bone-2)', color: 'var(--gold)', border: '1px solid var(--bone-2)' },
};

const chipBase: React.CSSProperties = {
  display: 'inline-block',
  padding: '2px 8px',
  borderRadius: 0,
  fontSize: 12,
  fontWeight: 600,
  whiteSpace: 'nowrap',
};

// ── VertragForm ────────────────────────────────────────────────────────────

interface VertragFormProps {
  onSaved: () => void;
  onCancel: () => void;
}

export const VertragForm: React.FC<VertragFormProps> = ({ onSaved, onCancel }) => {
  const [form, setForm] = useState({
    typ: 'EINKAUF',
    titel: '',
    beschreibung: '',
    partner_id: '',
    laufzeit_start: '',
    laufzeit_ende: '',
    laufzeit_monate: '',
    monatlicher_betrag: '',
    betrag_gesamt: '',
    kuendigungsfrist_tage: '30',
    auto_verlaengerung: false,
    status: 'ENTWURF',
    notizen: '',
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/vertraege`, {
        typ: form.typ,
        titel: form.titel,
        beschreibung: form.beschreibung || null,
        partner_id: form.partner_id ? parseInt(form.partner_id) : null,
        laufzeit_start: form.laufzeit_start || null,
        laufzeit_ende: form.laufzeit_ende || null,
        laufzeit_monate: form.laufzeit_monate ? parseInt(form.laufzeit_monate) : null,
        monatlicher_betrag: form.monatlicher_betrag ? parseFloat(form.monatlicher_betrag) : null,
        betrag_gesamt: form.betrag_gesamt ? parseFloat(form.betrag_gesamt) : null,
        kuendigungsfrist_tage: parseInt(form.kuendigungsfrist_tage),
        auto_verlaengerung: form.auto_verlaengerung,
        status: form.status,
        notizen: form.notizen || null,
      });
      onSaved();
    } finally {
      setSaving(false);
    }
  };

  const set = (field: string, value: string | boolean) =>
    setForm(f => ({ ...f, [field]: value }));

  return (
    <form onSubmit={handleSubmit} style={{ background: 'var(--bone-2)', padding: 16, borderRadius: 0, marginBottom: 16 }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <input
          required
          placeholder="Titel *"
          value={form.titel}
          onChange={e => set('titel', e.target.value)}
          style={{ padding: 8 }}
        />
        <select value={form.typ} onChange={e => set('typ', e.target.value)} style={{ padding: 8 }}>
          <option value="EINKAUF">Einkauf</option>
          <option value="VERKAUF">Verkauf</option>
        </select>
        <select value={form.status} onChange={e => set('status', e.target.value)} style={{ padding: 8 }}>
          <option value="ENTWURF">Entwurf</option>
          <option value="AKTIV">Aktiv</option>
          <option value="GEKUENDIGT">Gekündigt</option>
          <option value="ABGELAUFEN">Abgelaufen</option>
        </select>
        <input
          placeholder="Partner-ID"
          type="number"
          value={form.partner_id}
          onChange={e => set('partner_id', e.target.value)}
          style={{ padding: 8 }}
        />
        <input
          placeholder="Laufzeit Start"
          type="date"
          value={form.laufzeit_start}
          onChange={e => set('laufzeit_start', e.target.value)}
          style={{ padding: 8 }}
        />
        <input
          placeholder="Laufzeit Ende"
          type="date"
          value={form.laufzeit_ende}
          onChange={e => set('laufzeit_ende', e.target.value)}
          style={{ padding: 8 }}
        />
        <input
          placeholder="Laufzeit (Monate)"
          type="number"
          value={form.laufzeit_monate}
          onChange={e => set('laufzeit_monate', e.target.value)}
          style={{ padding: 8 }}
        />
        <input
          placeholder="Monatlicher Betrag €"
          type="number"
          step="0.01"
          value={form.monatlicher_betrag}
          onChange={e => set('monatlicher_betrag', e.target.value)}
          style={{ padding: 8 }}
        />
        <input
          placeholder="Gesamtbetrag €"
          type="number"
          step="0.01"
          value={form.betrag_gesamt}
          onChange={e => set('betrag_gesamt', e.target.value)}
          style={{ padding: 8 }}
        />
        <input
          placeholder="Kündigungsfrist (Tage)"
          type="number"
          value={form.kuendigungsfrist_tage}
          onChange={e => set('kuendigungsfrist_tage', e.target.value)}
          style={{ padding: 8 }}
        />
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, padding: 8 }}>
          <input
            type="checkbox"
            checked={form.auto_verlaengerung}
            onChange={e => set('auto_verlaengerung', e.target.checked)}
          />
          Auto-Verlängerung
        </label>
        <textarea
          placeholder="Beschreibung"
          value={form.beschreibung}
          onChange={e => set('beschreibung', e.target.value)}
          style={{ padding: 8, resize: 'vertical' }}
        />
        <textarea
          placeholder="Notizen"
          value={form.notizen}
          onChange={e => set('notizen', e.target.value)}
          style={{ padding: 8, resize: 'vertical' }}
        />
      </div>
      <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
        <button type="submit" className="k-btn k-btn--primary" disabled={saving}>
          {saving ? 'Speichert…' : 'Speichern'}
        </button>
        <button type="button" className="k-btn k-btn--ghost" onClick={onCancel}>
          Abbrechen
        </button>
      </div>
    </form>
  );
};

// ── VertragList ────────────────────────────────────────────────────────────

const VertragList: React.FC = () => {
  const [vertraege, setVertraege] = useState<Vertrag[]>([]);
  const [loading, setLoading] = useState(true);
  const [fehler, setFehler] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [filterTyp, setFilterTyp] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [akteId, setAkteId] = useState<number | null>(null);

  const load = () => {
    setLoading(true);
    setFehler(null);
    setAkteId(null);
    axios
      .get<Vertrag[]>(`${API_URL}/api/vertraege`, {
        params: {
          ...(filterTyp && { typ: filterTyp }),
          ...(filterStatus && { status: filterStatus }),
        },
      })
      .then(r => setVertraege(r.data))
      .catch((err: unknown) => {
        const msg = axios.isAxiosError(err) ? (err.response?.data?.detail ?? err.message) : 'Unbekannter Fehler';
        setFehler(`Verträge konnten nicht geladen werden: ${msg}`);
      })
      .finally(() => setLoading(false));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [filterTyp, filterStatus]);

  const handleKuendigen = async (v: Vertrag) => {
    if (!window.confirm(`Vertrag "${v.titel}" wirklich kündigen?`)) return;
    await axios.delete(`${API_URL}/api/vertraege/${v.vertrag_id}`);
    load();
  };

  return (
    <div style={{ padding: 16 }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h2 style={{ margin: 0 }}>Verträge</h2>
        <button
          className={`k-btn ${showForm ? 'k-btn--ghost' : 'k-btn--primary'}`}
          onClick={() => setShowForm(!showForm)}
        >
          {showForm ? 'Abbrechen' : '+ Neuer Vertrag'}
        </button>
      </div>

      {/* Filter row */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <select
          value={filterTyp}
          onChange={e => setFilterTyp(e.target.value)}
          style={{ padding: '6px 10px' }}
        >
          <option value="">Alle Typen</option>
          <option value="EINKAUF">Einkauf</option>
          <option value="VERKAUF">Verkauf</option>
        </select>
        <select
          value={filterStatus}
          onChange={e => setFilterStatus(e.target.value)}
          style={{ padding: '6px 10px' }}
        >
          <option value="">Alle Status</option>
          <option value="ENTWURF">Entwurf</option>
          <option value="AKTIV">Aktiv</option>
          <option value="GEKUENDIGT">Gekündigt</option>
          <option value="ABGELAUFEN">Abgelaufen</option>
        </select>
      </div>

      {/* Form */}
      {showForm && (
        <VertragForm
          onSaved={() => { setShowForm(false); load(); }}
          onCancel={() => setShowForm(false)}
        />
      )}

      {/* Table */}
      {loading ? (
        <div style={{ padding: 16 }}>Lade Verträge…</div>
      ) : fehler ? (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px',
          background: 'var(--bone-2)', border: '1px solid var(--bone-3)', borderRadius: 0,
          fontSize: 13, color: 'var(--red)',
        }}>
          <span>{fehler}</span>
          <button
            className="k-btn k-btn--danger k-btn--sm"
            onClick={load}
            style={{ marginLeft: 'auto', whiteSpace: 'nowrap' }}
          >
            Erneut versuchen
          </button>
        </div>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: 'var(--bone-3)' }}>
              <th style={{ padding: '8px 12px', textAlign: 'left' }}>Titel</th>
              <th style={{ padding: '8px 12px', textAlign: 'left' }}>Typ</th>
              <th style={{ padding: '8px 12px', textAlign: 'left' }}>Status</th>
              <th style={{ padding: '8px 12px', textAlign: 'right' }}>Monatlich</th>
              <th style={{ padding: '8px 12px', textAlign: 'left' }}>Laufzeit Ende</th>
              <th style={{ padding: '8px 12px', textAlign: 'left' }}>Nächstes Kündigung</th>
              <th style={{ padding: '8px 12px', textAlign: 'center' }}>Aktionen</th>
            </tr>
          </thead>
          <tbody>
            {vertraege.map(v => {
              const kuendigungDays = daysUntil(v.naechstes_kuendigungsdatum);
              const kuendigungSoon = kuendigungDays !== null && kuendigungDays <= 7;
              return (
                <React.Fragment key={v.vertrag_id}>
                  <tr style={{ borderBottom: '1px solid var(--bone-3)' }}>
                    <td style={{ padding: '8px 12px', fontWeight: 500 }}>{v.titel}</td>
                    <td style={{ padding: '8px 12px' }}>
                      <span style={{ ...chipBase, ...(TYP_CHIP[v.typ] ?? {}) }}>{v.typ}</span>
                    </td>
                    <td style={{ padding: '8px 12px' }}>
                      <span style={{ ...chipBase, ...(STATUS_CHIP[v.status] ?? {}) }}>{v.status}</span>
                    </td>
                    <td style={{ padding: '8px 12px', textAlign: 'right' }}>
                      {formatBetrag(v.monatlicher_betrag)}
                    </td>
                    <td style={{ padding: '8px 12px' }}>{formatDate(v.laufzeit_ende)}</td>
                    <td style={{ padding: '8px 12px' }}>
                      {v.naechstes_kuendigungsdatum ? (
                        <span style={kuendigungSoon ? { color: 'var(--red)', fontWeight: 700 } : {}}>
                          {formatDate(v.naechstes_kuendigungsdatum)}
                        </span>
                      ) : '—'}
                    </td>
                    <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                      {v.status === 'AKTIV' && (
                        <button
                          className="k-btn k-btn--danger k-btn--sm"
                          onClick={() => handleKuendigen(v)}
                        >
                          Kündigen
                        </button>
                      )}{' '}
                      <button
                        className="k-btn k-btn--ghost k-btn--sm"
                        onClick={() => setAkteId(akteId === v.vertrag_id ? null : v.vertrag_id)}
                      >
                        {akteId === v.vertrag_id ? 'Akte schließen' : 'Akte'}
                      </button>
                    </td>
                  </tr>
                  {akteId === v.vertrag_id && (
                    <tr>
                      <td colSpan={7} style={{ background: 'var(--bone-2)', padding: '12px 24px' }}>
                        <AktivitaetenBlock entitaetTyp="vertrag" entitaetId={v.vertrag_id} />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
            {vertraege.length === 0 && (
              <tr>
                <td colSpan={7} style={{ padding: 24, textAlign: 'center', color: 'var(--ink-2)' }}>
                  Keine Verträge vorhanden.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default VertragList;
